import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  Grid,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
  Tooltip as MUITooltip,
} from '@mui/material';
import PageHeader from 'shared/components/PageHeader';
import { RiskOverviewService } from 'services/RiskOverviewService';
import { Utils } from 'shared/helpers';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CircleIcon from '@mui/icons-material/Circle';
import { DatePicker } from '@mui/x-date-pickers';
import { ReactComponent as Warning } from 'assets/images/ic_warning.svg';
import dayjs from 'dayjs';
import MemoryIcon from '@mui/icons-material/Memory';
import { useSize } from 'shared/components/Hooks';
import { AppSelectWithKeys } from 'shared/components/AppComponents';

const IMPACTS = ['Critical', 'High', 'Moderate', 'Low'];
const COLUMNS = ['Rare Unlikely', 'Possible', 'Likely', 'Almost Certain'];
const AI_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Internal AI', value: 'Internal-AI' },
  { label: 'Third-Party AI', value: 'Third-Party-AI' },
];
const FILTER_TYPES = [
  { label: 'Last 30 days', value: 'LAST_30_DAYS' },
  { label: 'Last 6 months', value: 'LAST_6_MONTHS' },
  { label: 'Last 12 months', value: 'LAST_12_MONTHS' },
  { label: 'All time', value: 'ALL_TIME' },
];
const ALL_TIME_FROM = '2020-01-01';

const getCount = (arrayMatrix: any, impact: any, likelihood: any): any => {
  if (likelihood === 'Rare Unlikely') {
    const rare = arrayMatrix?.find(
      (d: any) => d._id.impact === impact && d._id.likelihood === 'Rare'
    );
    const unlikely = arrayMatrix?.find(
      (d: any) => d._id.impact === impact && d._id.likelihood === 'Unlikely'
    );
    return (rare?.count || 0) + (unlikely?.count || 0);
  }

  const match = arrayMatrix?.find(
    (d: any) => d._id.impact === impact && d._id.likelihood === likelihood
  );
  return match?.count || 0;
};

const RiskMatrix = ({ label, arrayMatrix }: any): any => {
  const total = arrayMatrix?.reduce((total: any, d: any) => total + d.count, 0);

  return (
    <Box className={`risk-impactness ${label.toLowerCase()}`}>
      <Box className="info">
        <Stack direction="row" className="info-total">
          <Typography variant="body2">{label} Risk Matrix</Typography>
          <Typography variant="body1">Total: {total}</Typography>
        </Stack>
      </Box>
      <Stack direction="row" className="risk-over-impact">
        <Box className="impact">
          <Typography component="span">Impact</Typography>
        </Box>
        <Box className="relation">
          <Stack direction="row" className="relation-heading">
            {COLUMNS.map((col) => (
              <Typography key={col} variant="body2">
                {col}
              </Typography>
            ))}
          </Stack>

          {IMPACTS.map((impact) => (
            <Stack
              key={impact}
              direction="row"
              className={`relation-to-${impact.toLowerCase()}`}
            >
              <Typography variant="body1" component="span">
                {impact}
              </Typography>
              {COLUMNS.map((col) => {
                const count = getCount(arrayMatrix, impact, col);
                return (
                  <Box key={col} className="square">
                    <Typography component="span">{count}</Typography>
                  </Box>
                );
              })}
            </Stack>
          ))}

          <Box className="likelihood">
            <Typography component="span">Likelihood</Typography>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

const riskTierOrder = ['Low', 'Moderate', 'High', 'Critical'];

const defaultRiskTier: any = [
  {
    count: 0,
    riskTier: 'Low',
  },

  {
    count: 0,
    riskTier: 'Moderate',
  },

  {
    count: 0,
    riskTier: 'High',
  },
  {
    count: 0,
    riskTier: 'Critical',
  },
];

const riskTierColor: any = {
  Low: { bg: '#dcf5e7', color: '#1a5c35' },
  Moderate: { bg: '#fdf6cc', color: '#7a5e0a' },
  High: { bg: '#fde8cc', color: '#8b3a0f' },
  Critical: { bg: '#fde0e0', color: '#8b1515' },
};

const handleChangeFilterType = (start: any, end: any): any => {
  if (!start || !end) return 'CUSTOM';
  const today = dayjs().startOf('day');
  const fromDay = dayjs(start).startOf('day');
  const toDay = dayjs(end).startOf('day');

  if (toDay.isSame(today) && fromDay.isSame(today.subtract(30, 'day')))
    return 'LAST_30_DAYS';

  if (toDay.isSame(today) && fromDay.isSame(today.subtract(6, 'month')))
    return 'LAST_6_MONTHS';

  if (toDay.isSame(today) && fromDay.isSame(today.subtract(12, 'month')))
    return 'LAST_12_MONTHS';

  if (toDay.isSame(today) && fromDay.isSame(dayjs(ALL_TIME_FROM)))
    return 'ALL_TIME';

  return 'CUSTOM';
};

const handleDatesPreset = (preset: any): any => {
  const today = dayjs();
  switch (preset) {
    case 'LAST_30_DAYS':
      return { from: today.subtract(30, 'day'), to: today };
    case 'LAST_6_MONTHS':
      return { from: today.subtract(6, 'month'), to: today };
    case 'LAST_12_MONTHS':
      return { from: today.subtract(12, 'month'), to: today };
    case 'ALL_TIME':
      return { from: dayjs(ALL_TIME_FROM), to: today };
    default:
      return {
        from: today.subtract(12, 'month'),
        to: today,
      };
  }
};

const riskTierMapping = (riskTier = defaultRiskTier): ReactElement => {
  const sortedRiskTier = [...(riskTier ?? [])]
    .filter(({ riskTier: tier }) => tier !== 'Medium')
    .sort(
      (a, b) =>
        riskTierOrder.indexOf(a.riskTier) - riskTierOrder.indexOf(b.riskTier)
    );

  return (
    <>
      {sortedRiskTier.map(({ count, riskTier: tier }: any) => {
        const { bg, color } = riskTierColor[tier] || {};
        return (
          <Grid item key={tier}>
            <Box
              sx={{
                background: bg,
                color,
                borderRadius: 3,
                padding: '20px 16px',
                textAlign: 'center',
                minWidth: 100,
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: 26 }}>
                {count}
              </Typography>
              <Typography variant="body2" fontWeight="600" mt={2}>
                {tier}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </>
  );
};

const RiskOverview = (): ReactElement => {
  const [riskData, setRiskData] = useState<any>(null);

  const defaultPreset: any = 'LAST_12_MONTHS';
  const defaultDates = handleDatesPreset(defaultPreset);
  const [activeFilterType, setActiveFilterType] = useState<any>(defaultPreset);
  const [activeAiFilter, setActiveAiFilter] = useState<any>('All');
  const [startDate, setStartDate] = useState<any>(defaultDates.from);
  const [endDate, setEndDate] = useState<any>(defaultDates.to);

  const theme = useTheme();
  const { isDesktop } = useSize();
  const mergeChartData = (totalRisk: any[], residualRisk: any[]): any =>
    totalRisk &&
    totalRisk.map((item) => {
      const residual = residualRisk.find(
        (r) => r.month === item.month && r.year === item.year
      );
      return {
        name: `${item.month} ${item.year}`,
        totalRisk: item.totalRisk,
        residualRisk: residual?.residualRiskCount ?? 0,
      };
    });

  const chartData: any = mergeChartData(
    riskData?.riskRegister?.monthWiseTotalRisk,
    riskData?.riskRegister?.monthWiseResidualRisk
  );

  // DATE AND TYPE FILTER -------------
  const handleFilterTypeClick = (preset: any): any => {
    if (preset === 'CUSTOM') return;
    const { from, to } = handleDatesPreset(preset as any);
    setActiveFilterType(preset);
    setStartDate(from);
    setEndDate(to);
  };

  const isDayJS = (val: any): any => {
    if (!val) return null;
    const date = dayjs(val);
    return date.isValid() ? date : null;
  };

  const handleStartDateChange = (val: any): any => {
    const date = isDayJS(val);
    if (date) return;
    setStartDate(date);
    setActiveFilterType(handleChangeFilterType(date, endDate));
  };

  const handleEndDateChange = (val: any): any => {
    const date = isDayJS(val);
    if (!date) return;
    setEndDate(date);
    setActiveFilterType(handleChangeFilterType(startDate, date));
  };

  useEffect(() => {
    const from = dayjs(startDate);
    const to = dayjs(endDate);

    const fromDate =
      activeFilterType === 'CUSTOM' && from.isValid()
        ? from.format('YYYY-MM-DD')
        : undefined;
    const toDate =
      activeFilterType === 'CUSTOM' && to.isValid()
        ? to.format('YYYY-MM-DD')
        : undefined;

    RiskOverviewService.getAll(
      activeFilterType,
      activeAiFilter,
      fromDate,
      toDate
    )
      .then((res: any) => {
        setRiskData(res.data.data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      });
  }, [activeFilterType, startDate, endDate, activeAiFilter]);

  return (
    <>
      <PageHeader
        title="Risk Overview"
        customDescription="Monitor and analyze your organisation's risk landscape"
        customStyleTitleSection={{
          flexBasis: 'fit-content',
        }}
        customStyleActionSection={{
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column-reverse !important',
          },

          alignItems: 'flex-end !important',
        }}
        actions={
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2}>
              <AppSelectWithKeys
                size="small"
                value={activeAiFilter}
                onChange={(e: any) => {
                  setActiveAiFilter(e.target.value);
                }}
                items={AI_FILTERS}
                titleKey="label"
                valueKey="value"
              />
              <AppSelectWithKeys
                size="small"
                value={activeFilterType}
                onChange={(e: any) => {
                  handleFilterTypeClick(e.target.value);
                }}
                items={FILTER_TYPES}
                titleKey="label"
                valueKey="value"
              />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                margin: '10px 0',
              }}
            >
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ width: 160, marginBottom: '0' }}
                  />
                )}
              />
              <Typography variant="body2" color="#7b696e">
                to
              </Typography>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ width: 160, marginBottom: '0' }}
                  />
                )}
              />
            </Box>
          </Stack>
        }
      />

      <Box className="risk-management risk-overview">
        <HeadTag title="Risk Overview" />

        <Box className="risk-overview-wrapper">
          <Box>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={12} md={3}>
                <Box
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 2,
                    p: 4,
                    height: { sm: '90%', lg: '85%' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Open Risks
                    </Typography>
                    <MUITooltip
                      title="Open Risk includes all risks that have not been archived, accepted, or mitigated"
                      placement="top"
                    >
                      <SvgIcon
                        component={Warning}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                      />
                    </MUITooltip>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '26px',
                        fontWeight: 700,
                      }}
                      mt={4}
                    >
                      {riskData?.riskOverview?.openRisks ?? 0}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#7b696e',
                        fontSize: 12,
                      }}
                      mt={4}
                    >
                      Risks requiring attention
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} md={9}>
                <Box
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 2,
                    p: 4,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      AI Systems by Risk Tier
                    </Typography>
                    <MUITooltip
                      title="Count of active AI systems in each risk tier category"
                      placement="top"
                    >
                      <MemoryIcon sx={{ color: '#7b696e' }} fontSize="small" />
                    </MUITooltip>
                  </Box>
                  <Box mt={4}>
                    <Grid
                      container
                      spacing={2}
                      sx={{ justifyContent: 'center', gap: '1rem' }}
                    >
                      {riskTierMapping(riskData?.aiSystemByRiskTier)}
                    </Grid>
                  </Box>
                  <Typography
                    sx={{
                      color: '#7b696e',
                      fontSize: 12,
                    }}
                    mt={4}
                  >
                    Active AI systems categorized by assigned risk level
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className="risk-overview-heading" mt={5}>
            <Stack direction="row">
              <Typography component="span">Risk Over Time</Typography>
              <Stack direction="row" spacing={2} className="risk-indicator">
                <Chip
                  icon={<CircleIcon style={{ color: '#DE3A3A' }} />}
                  label="Risk Discovered"
                  variant="outlined"
                />
                <Chip
                  icon={<CircleIcon style={{ color: '#76A81F' }} />}
                  label="Residual Risk"
                  variant="outlined"
                />
              </Stack>
            </Stack>
          </Box>

          <Box
            className="risk-statistic"
            sx={{ background: '#f1f5f4', padding: '33px 0' }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
              >
                <CartesianGrid />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 'auto']}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid rgba(0,0,0,0.1)',
                    fontSize: 13,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="totalRisk"
                  name="Total Risk"
                  stroke="#DE3A3A"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#DE3A3A' }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="residualRisk"
                  name="Residual Risk"
                  stroke="#76A81F"
                  strokeWidth={2.5}
                  strokeDasharray="6 3"
                  dot={{ r: 4, fill: '#76A81F' }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          <Stack
            direction={isDesktop ? 'row' : 'column'}
            spacing={10}
            className="risk-statuses"
          >
            <RiskMatrix
              label="Untreated"
              arrayMatrix={riskData?.riskMatrix?.untreated}
            />
            <RiskMatrix
              label="Treated"
              arrayMatrix={riskData?.riskMatrix?.treated}
            />
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default RiskOverview;
